import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Col, Container, Row, Card, Badge, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaHotel } from 'react-icons/fa';
import { FaBus } from 'react-icons/fa6';
import { regionsService, excursionsService } from '@/api/services';
import { useHotelsData, useRegionsList } from '@/hooks';
import { getRegionImage, toPathSegment } from '@/utils/imageUrl';

// Fallback images for regions
import tour1 from '@/assets/images/category/tour/01.jpg';
import tour2 from '@/assets/images/category/tour/02.jpg';
import tour3 from '@/assets/images/category/tour/03.jpg';
import tour4 from '@/assets/images/category/tour/04.jpg';
import tour5 from '@/assets/images/category/tour/05.jpg';
import tour6 from '@/assets/images/category/tour/06.jpg';

const fallbackRegionImages = [tour1, tour2, tour3, tour4, tour5, tour6];

function filterActiveRegions(regionsData) {
  return (Array.isArray(regionsData) ? regionsData : [])
    .filter((r) => r.is_active === 1 || r.is_active === true)
    .sort((a, b) => {
      const aHotels = a.hotel_count || 0;
      const bHotels = b.hotel_count || 0;
      if (bHotels !== aHotels) return bHotels - aHotels;
      return (a.order || 0) - (b.order || 0);
    });
}

const DestinationsOverview = ({ sectionData = null }) => {
  const bgColor = sectionData?.background_color || 'light';
  const bgEffect = sectionData?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  const { hotels } = useHotelsData();
  const { data: regionsData, loading: regionsLoading, error: regionsError } = useRegionsList();
  const [regions, setRegions] = useState(() => filterActiveRegions(regionsData));
  const [regionHighlights, setRegionHighlights] = useState({}); // Store highlights by region ID
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(() => regionsLoading && !Array.isArray(regionsData));
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (regionsLoading) {
          return;
        }

        if (regionsError) {
          setError(regionsError?.message || 'Failed to load regions');
          setRegions([]);
          return;
        }

        const regionsList = Array.isArray(regionsData) ? regionsData : [];

      // Filter active regions only
      const activeRegions = regionsList
        .filter(r => r.is_active === 1 || r.is_active === true)
        .sort((a, b) => {
          // Sort by hotel_count (descending - most hotels first)
          const aHotels = a.hotel_count || 0;
          const bHotels = b.hotel_count || 0;
          if (bHotels !== aHotels) {
            return bHotels - aHotels;
          }
          // If hotel counts are equal, fall back to order
          return (a.order || 0) - (b.order || 0);
        });

      setRegions(activeRegions);

      // Fetch highlights for each region
      const highlightsMap = {};
      await Promise.all(
        activeRegions.map(async (region) => {
          // Fetch highlights
          try {
            const highlightsRes = await regionsService.getHighlights(region.id);
            const highlightsData = highlightsRes.data?.data || highlightsRes.data || [];
            // Filter active highlights if needed, or just use all
            const activeHighlights = Array.isArray(highlightsData) 
              ? highlightsData.filter(h => h.is_active !== 0 && h.is_active !== false)
              : [];
            highlightsMap[region.id] = activeHighlights.length > 0 ? activeHighlights : null;
          } catch (err) {
            // If highlights endpoint doesn't exist, check if highlights are in the region object
            console.warn(`Failed to fetch highlights for region ${region.id}:`, err);
            // Check if highlights are already in the region object
            if (region.highlights && Array.isArray(region.highlights) && region.highlights.length > 0) {
              highlightsMap[region.id] = region.highlights;
            } else {
              highlightsMap[region.id] = null;
            }
          }
        })
      );
      setRegionHighlights(highlightsMap);
      
      // Fetch pickups
      try {
        const pickupRes = await excursionsService.getAllPickups();
        if (pickupRes.data?.success && pickupRes.data?.data) {
          const allPickups = Array.isArray(pickupRes.data.data)
            ? pickupRes.data.data
            : [];
          setPickups(allPickups);
        }
      } catch (err) {
        console.warn('Failed to fetch excursion pickups:', err);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load regions');
      setRegions([]);
    } finally {
      setLoading(false);
    }
    };

    fetchData();
  }, [regionsData, regionsLoading, regionsError]);
  
  // Calculate excursion count for each region
  const regionsWithExcursionCount = useMemo(() => {
    return regions.map((region) => {
      // Get hotels in this region
      const hotelsInRegion = hotels.filter(
        (hotel) =>
          hotel.region?.id === region.id ||
          hotel.destination?.region_id === region.id
      );
      
      // Count unique excursions with pickups from hotels in this region
      const hotelIdsInRegion = new Set(hotelsInRegion.map(h => String(h.id)));
      const excursionsWithPickups = new Set();
      
      pickups.forEach(pickup => {
        const pickupAccId = String(pickup.acc_id || pickup.accommodation_id || pickup.hotel_id || '');
        if (hotelIdsInRegion.has(pickupAccId)) {
          const excId = String(pickup.exc_id || pickup.excursion_id || '');
          if (excId) {
            excursionsWithPickups.add(excId);
          }
        }
      });
      
      return {
        ...region,
        excursionCount: excursionsWithPickups.size
      };
    });
  }, [regions, hotels, pickups]);

  const getRegionImageUrl = (region, index) => {
    return getRegionImage(region, fallbackRegionImages[index % fallbackRegionImages.length]);
  };

  if (loading) {
    return (
      <section className={`py-5 py-lg-6 ${bgClass} destinations-overview-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
        {hasDotsEffect && (
          <>
            <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
            <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
          </>
        )}
        <Container>
          <Row className="mb-5">
            <Col lg={10} className="mx-auto text-center">
              <div className="d-flex justify-content-center">
                <Spinner animation="border" variant="primary" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`py-5 py-lg-6 ${bgClass} destinations-overview-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
        {hasDotsEffect && (
          <>
            <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
            <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
          </>
        )}
        <Container>
          <Row>
            <Col lg={10} className="mx-auto">
              <div className="alert alert-warning" role="alert">
                <h5 className="alert-heading">Unable to Load Content</h5>
                <p className="mb-0">{error}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  if (regions.length === 0) {
    return null;
  }

  const badgeText = sectionData?.subtitle?.trim()
    ? sectionData.subtitle
    : (sectionData?.section_name?.trim() ? sectionData.section_name : 'Explore Our Regions');
  const titleText = sectionData?.title?.trim() ? sectionData.title : 'Discover Beautiful East Crete';
  const introText = sectionData?.intro_text?.trim()
    ? sectionData.intro_text
    : 'Explore our regions and discover their unique places of interest, each offering distinct experiences and authentic Cretan hospitality';

  return (
    <section className={`py-5 py-lg-6 ${bgClass} destinations-overview-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container>
        <Row className="mb-5">
          <Col>
            <Badge bg="primary" className="bg-opacity-10 text-primary mb-3 px-3 py-2">
              <FaMapMarkerAlt className="me-2" />
              {badgeText}
            </Badge>
            <h1 className="sec-title mb-3">
              {titleText}
            </h1>
            {introText && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: introText }} />
            )}
          </Col>
        </Row>

        <div className="d-flex flex-column gap-4">
          {regionsWithExcursionCount.map((region, regionIdx) => {
            const regionImage = getRegionImageUrl(region, regionIdx);

            const safeRegionSlug = toPathSegment(region.slug, '');
            const regionHref = safeRegionSlug
              ? `/east-crete/${encodeURIComponent(safeRegionSlug)}`
              : '/east-crete';

            return (
              <Link key={region.id} href={regionHref} className="text-decoration-none text-reset d-block region-card-link">
              <Card className="region-card">
                {/* Full Background Image */}
                <div 
                  className="region-background-image"
                  style={{
                    backgroundImage: `url(${regionImage})`
                  }}
                >
                  {/* Gradient Overlay for better text readability */}
                  <div className="region-overlay"></div>
                  
                  {/* Content Container */}
                  <div className="region-content-container">
                    {/* Two Column Layout */}
                    <div className="region-columns-layout">
                      {/* Left Column */}
                      <div className="region-left-column">
                        {/* Region Title Overlay - Top */}
                        <div className="region-title-overlay">
                          <div className="region-title-content">
                            <h3>{region.name}</h3>
                          </div>
                          {(region.tagline || region.description) && (
                            <p className="region-tagline" dangerouslySetInnerHTML={{ __html: region.tagline || region.description }} />
                          )}
                        </div>

                        {/* Stats Badges - Bottom, Overlay on Image */}
                        <div className="region-stats-badges d-flex gap-2 align-items-center">
                          {region.hotel_count > 0 && (
                            <span className="stat-badge">
                              <FaHotel />
                              {region.hotel_count} Hotel{region.hotel_count !== 1 ? 's' : ''}
                            </span>
                          )}
                          {region.excursionCount > 0 && (
                            <span className="stat-badge">
                              <FaBus />
                              {region.excursionCount} Excursion{region.excursionCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="region-right-column">
                        {/* Region Info Card - Top */}
                        <div className="region-info-overlay">
                          <div className="region-content">
                            {(() => {
                              // Check for highlights from fetched data or region object
                              const highlights = regionHighlights[region.id] || 
                                                region.highlights || 
                                                region.region_highlights || 
                                                region.highlights_list ||
                                                (region.raw && region.raw.highlights) ||
                                                null;
                              
                              if (highlights && Array.isArray(highlights) && highlights.length > 0) {
                                return (
                                  <ul className="region-highlights">
                                    {highlights.map((highlight, idx) => {
                                      const highlightTitle = typeof highlight === 'string' 
                                        ? highlight 
                                        : (highlight.title || highlight.name || highlight.text || '');
                                      const highlightDescription = typeof highlight === 'object' 
                                        ? (highlight.description || highlight.desc || '')
                                        : '';
                                      
                                      return (
                                        <li key={idx} className="region-highlight-item">
                                          <span className="region-highlight-dot"></span>
                                          <div className="region-highlight-content">
                                            <span className="region-highlight-text">
                                              {highlightTitle}
                                            </span>
                                            {highlightDescription && (
                                              <span className="region-highlight-description">
                                                {highlightDescription}
                                              </span>
                                            )}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                );
                              } else if (region.description) {
                                return (
                                  <p className="region-description" dangerouslySetInnerHTML={{ __html: region.description }} />
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              </Link>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default DestinationsOverview;
